import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Search, X } from "lucide-react"

import { Button, Input } from "@f2e/ui/components/base"

export interface CustomerSearchProps {
  value: string
  identityMasked: boolean
  onSearch: (query: string) => void
}

export const CustomerSearch = ({
  value,
  identityMasked,
  onSearch,
}: CustomerSearchProps) => {
  const { t } = useTranslation()
  const [input, setInput] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => setInput(value), [value])

  return (
    <>
      <form
        role="search"
        aria-label={t("historyList.customerSearch.placeholder")}
        className="mt-3 flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (identityMasked) return
          const query = input.trim()
          setInput(query)
          onSearch(query)
        }}
      >
        <div className="relative min-w-0 flex-1">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            ref={inputRef}
            type="search"
            aria-label={t("historyList.customerSearch.placeholder")}
            placeholder={t("historyList.customerSearch.placeholder")}
            aria-describedby={
              identityMasked ? "customer-search-masked" : undefined
            }
            value={identityMasked ? "" : input}
            onChange={(event) => setInput(event.target.value)}
            disabled={identityMasked}
            className="h-9 pl-9 pr-9 [&::-webkit-search-cancel-button]:appearance-none"
          />
          {!identityMasked && (input || value) && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
              aria-label={t("historyList.customerSearch.clear")}
              onClick={() => {
                setInput("")
                onSearch("")
                inputRef.current?.focus()
              }}
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Button
          type="submit"
          variant="outline"
          className="h-9"
          disabled={identityMasked}
        >
          {t("historyList.customerSearch.submit")}
        </Button>
      </form>
      {identityMasked && (
        <p
          id="customer-search-masked"
          className="mt-2 text-xs text-muted-foreground"
        >
          {t("historyList.customerSearch.masked")}
        </p>
      )}
    </>
  )
}
